'use client';

import { AlertTriangle, X } from 'lucide-react';
import { type CSSProperties, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import styles from './alarm-notification-modal.module.css';

export interface AlarmNotificationModalProps {
  className?: string;
  compact?: boolean;
  detailLabel?: string;
  deviceId?: string | null;
  headline?: string | null;
  inline?: boolean;
  message?: string | null;
  open: boolean;
  severityLabel?: string;
  style?: CSSProperties;
  timestamp?: Date | string | null;
  onClose: () => void;
  onViewDetails?: () => void;
}

interface AlarmNotificationDetailButtonProps {
  label?: string;
  onClick?: () => void;
}

/**
 * 알람 상세 화면으로 이동할 때 사용할 공용 버튼입니다.
 */
export function AlarmNotificationDetailButton({ label = '자세히보기', onClick }: AlarmNotificationDetailButtonProps) {
  return (
    <button type="button" className={styles.detailButton} onClick={onClick}>
      {label}
    </button>
  );
}

/**
 * 비정상 값이 들어와도 화면에는 항상 안전한 문자열만 노출합니다.
 */
function normalizeText(value: string | null | undefined, fallback = '') {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

/**
 * 알람 본문 문구를 장치 ID와 결합해 화면 표시용 제목으로 변환합니다.
 */
function buildHeadline({ deviceId, headline, message }: Pick<AlarmNotificationModalProps, 'deviceId' | 'headline' | 'message'>) {
  const normalizedHeadline = normalizeText(headline);
  if (normalizedHeadline) {
    return normalizedHeadline;
  }

  const normalizedMessage = normalizeText(message, '알림이 수신되었습니다.');
  const normalizedDeviceId = normalizeText(deviceId);

  if (!normalizedDeviceId) {
    return normalizedMessage;
  }

  return `장치 ID : ${normalizedDeviceId} ${normalizedMessage}`;
}

/**
 * 알람 시각을 이미지 시안과 유사한 포맷으로 표준화합니다.
 */
function formatAlarmTimestamp(value: AlarmNotificationModalProps['timestamp']) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : '';
  }

  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const sec = String(date.getSeconds()).padStart(2, '0');

  return `${yyyy}.${mm}.${dd} ${hh}:${min}:${sec}`;
}

/**
 * 알람 심각도 라벨을 색상 테마 클래스로 변환합니다.
 */
function resolveSeverityClassName(severityLabel: string) {
  if (severityLabel.includes('해제')) {
    return styles.severityMeasure;
  }

  if (severityLabel.includes('중대경보')) {
    return styles.severityCritical;
  }

  if (severityLabel.includes('경고') || severityLabel.includes('경보')) {
    return styles.severityWarning;
  }

  if (severityLabel.includes('주의')) {
    return styles.severityCaution;
  }

  if (severityLabel.includes('정보')) {
    return styles.severityInfo;
  }

  if (severityLabel.includes('계측')) {
    return styles.severityMeasure;
  }

  return styles.severityDefault;
}

/**
 * 포털 대상이 준비된 이후에만 브라우저 DOM에 렌더링합니다.
 */
export function AlarmNotificationModal({
  className,
  compact = false,
  detailLabel: _detailLabel = '자세히보기',
  deviceId,
  headline,
  inline = false,
  message,
  open,
  severityLabel = '경고',
  style,
  timestamp,
  onClose,
  onViewDetails: _onViewDetails,
}: AlarmNotificationModalProps) {
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    // 오버레이가 없는 고정형 알람이므로 ESC 닫기를 직접 처리합니다.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const resolvedSeverityLabel = normalizeText(severityLabel, '경고');
  const resolvedHeadline = buildHeadline({ deviceId, headline, message });
  const resolvedMessage = normalizeText(message);
  const shouldShowMessage = resolvedMessage.length > 0 && resolvedMessage !== resolvedHeadline;
  const resolvedTimestamp = formatAlarmTimestamp(timestamp);
  const severityClassName = resolveSeverityClassName(resolvedSeverityLabel);

  const modalNode = (
    <div
      className={cn(styles.viewport, inline && styles.inlineViewport, className)}
      role="alertdialog"
      aria-live="assertive"
      aria-modal="false"
      aria-label={`${resolvedSeverityLabel} 알림`}
      style={style}>
      <section className={cn(styles.card, severityClassName, compact && styles.compactCard)}>
        <div className={styles.accent} aria-hidden="true" />

        <div className={styles.body}>
          <div className={styles.content}>
            <div className={styles.headerRow}>
              <div className={styles.titleRow}>
                <span className={styles.iconWrap} aria-hidden="true">
                  <AlertTriangle size={28} strokeWidth={2.3} />
                </span>
                <strong className={styles.title}>{resolvedSeverityLabel}</strong>
              </div>

              <button type="button" className={styles.closeButton} onClick={onClose} aria-label="닫기">
                <X size={32} strokeWidth={1.7} />
              </button>
            </div>
            <p className={styles.headline}>{resolvedHeadline}</p>
            {shouldShowMessage ? <p className={styles.message}>{resolvedMessage}</p> : null}
          </div>

          {resolvedTimestamp ? (
            <div className={styles.footer}>
              <p className={styles.timestamp}>{resolvedTimestamp}</p>
              {/* 상세 화면 연결 정책이 확정되면 아래 버튼 주석을 해제합니다. */}
              {/* <AlarmNotificationDetailButton label={_detailLabel} onClick={_onViewDetails} /> */}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );

  if (inline) {
    return modalNode;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(modalNode, document.body);
}
